import React, { useRef, useEffect, useState } from 'react';
import AppConfig from '../config/appConfig'; // path আপনার ফোল্ডার স্ট্রাকচার অনুযায়ী ঠিক করুন

// Component to handle intersection observation and apply animations
const AnimatedOnScroll = ({ children, animationClass, delay = 0 }) => {
  const domRef = useRef();
  const [isVisible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        setVisible(true);
        observer.unobserve(domRef.current);
      }
    }, {
      threshold: 0.5
    });

    if (domRef.current) {
      observer.observe(domRef.current);
    }

    return () => {
      if (domRef.current) {
        observer.unobserve(domRef.current);
      }
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={domRef}
      className={`${isVisible ? animationClass : 'opacity-0 transform translate-y-4'} transition-all duration-700 ease-out`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// RefundPolicyPage component with Amazon-inspired design
const RefundPolicyPage = () => {
  const [policyData, setPolicyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReturnPolicy = async () => {
      try {
        const response = await fetch(`${AppConfig.API_BASE_URL}/api/return-policy`);
        const data = await response.json();

        if (data.success) {
          setPolicyData(data.policy);
        } else {
          setError(data.error || 'Failed to load return policy');
        }
      } catch (err) {
        setError('Failed to load return policy');
        console.error('Error fetching return policy:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchReturnPolicy();
  }, []);

  if (loading) {
    return (
      <div className="bg-white max-w-5xl mx-auto min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading return policy...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white max-w-5xl mx-auto min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white max-w-5xl mx-auto border border-gray-300 rounded-lg p-6 my-6">
      {/* Header Section - Amazon style */}
      <div className="border-b border-gray-300 pb-4 mb-8">
        <AnimatedOnScroll animationClass="animate-fade-in-down">
          <h1 className="text-3xl md:text-4xl font-normal text-gray-900 mb-2">
            {policyData?.title || 'Return Policy'}
          </h1>
          <p className="text-sm text-gray-600">
            {policyData?.lastUpdated || 'Last Updated: January 2026'}
          </p>
        </AnimatedOnScroll>
      </div>

      <div className="space-y-8">
        {/* Introduction */}
        <AnimatedOnScroll animationClass="animate-fade-in-up">
          <div className="bg-blue-50 border-l-4 border-blue-400 p-6">
            <p className="text-base text-gray-800 leading-relaxed">
              {policyData?.introduction || `At "${AppConfig.CompanyName}", we value your shopping experience. If you are not satisfied with your purchase, you can return or exchange the product following our return policy.`}
            </p>
          </div>
        </AnimatedOnScroll>

        {/* Return Timeline */}
        <AnimatedOnScroll animationClass="animate-fade-in-left" delay={100}>
          <div className="border-b border-gray-200 pb-6">
            <h2 className="text-2xl font-medium text-gray-900 mb-4">
              Return Timeline
            </h2>
            <p className="text-base text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{
              __html: policyData?.returnTimeline || 'You can apply for a return or exchange within <span class="font-semibold text-orange-600">7 (seven) days</span> from the delivery date. No requests will be accepted after this period.'
            }}></p>
          </div>
        </AnimatedOnScroll>

        {/* Return Conditions */}
        <AnimatedOnScroll animationClass="animate-fade-in-left" delay={200}>
          <div className="border-b border-gray-200 pb-6">
            <h2 className="text-2xl font-medium text-gray-900 mb-4">
              Return Conditions
            </h2>
            <div className="space-y-4">
              {policyData?.conditions && policyData.conditions.length > 0 ? (
                policyData.conditions.map((condition, index) => (
                  <AnimatedOnScroll
                    key={index}
                    animationClass="animate-fade-in-right"
                    delay={300 + (index * 50)}
                  >
                    <div className={`flex items-start space-x-3 ${condition.type === 'allowed'
                      ? 'bg-gray-50'
                      : 'bg-red-50 border-l-4 border-red-400'
                      } p-4 rounded`}>
                      <span className={`${condition.type === 'allowed'
                        ? 'text-green-600'
                        : 'text-red-600'
                        } text-xl flex-shrink-0 mt-1`}>
                        {condition.type === 'allowed' ? '✓' : '✕'}
                      </span>
                      <p className="text-gray-700">{condition.text}</p>
                    </div>
                  </AnimatedOnScroll>
                ))
              ) : (
                <>
                  <AnimatedOnScroll animationClass="animate-fade-in-right" delay={300}>
                    <div className="flex items-start space-x-3 bg-gray-50 p-4 rounded">
                      <span className="text-green-600 text-xl flex-shrink-0 mt-1">✓</span>
                      <p className="text-gray-700">The product must be unused and in its original condition.</p>
                    </div>
                  </AnimatedOnScroll>

                  <AnimatedOnScroll animationClass="animate-fade-in-right" delay={350}>
                    <div className="flex items-start space-x-3 bg-gray-50 p-4 rounded">
                      <span className="text-green-600 text-xl flex-shrink-0 mt-1">✓</span>
                      <p className="text-gray-700">You must record a video while unboxing the product. If there are any issues, send this video to our WhatsApp number and return within 7 days, otherwise {AppConfig.CompanyName} will not accept it.</p>
                    </div>
                  </AnimatedOnScroll>

                  <AnimatedOnScroll animationClass="animate-fade-in-right" delay={400}>
                    <div className="flex items-start space-x-3 bg-gray-50 p-4 rounded">
                      <span className="text-green-600 text-xl flex-shrink-0 mt-1">✓</span>
                      <p className="text-gray-700">All tags, labels, and original packaging must remain intact with the product.</p>
                    </div>
                  </AnimatedOnScroll>

                  <AnimatedOnScroll animationClass="animate-fade-in-right" delay={450}>
                    <div className="flex items-start space-x-3 bg-gray-50 p-4 rounded">
                      <span className="text-green-600 text-xl flex-shrink-0 mt-1">✓</span>
                      <p className="text-gray-700">You must provide proof of purchase receipt or order confirmation.</p>
                    </div>
                  </AnimatedOnScroll>

                  <AnimatedOnScroll animationClass="animate-fade-in-right" delay={500}>
                    <div className="flex items-start space-x-3 bg-red-50 p-4 rounded border-l-4 border-red-400">
                      <span className="text-red-600 text-xl flex-shrink-0 mt-1">✕</span>
                      <p className="text-gray-700">Personal care products, innerwear, and certain specific items are non-returnable for hygiene reasons.</p>
                    </div>
                  </AnimatedOnScroll>

                  <AnimatedOnScroll animationClass="animate-fade-in-right" delay={550}>
                    <div className="flex items-start space-x-3 bg-red-50 p-4 rounded border-l-4 border-red-400">
                      <span className="text-red-600 text-xl flex-shrink-0 mt-1">✕</span>
                      <p className="text-gray-700">Returns will not be accepted if you don't like the product after receiving it. Please review photos and videos carefully before purchasing. {AppConfig.CompanyName} provides products with photos and videos.</p>
                    </div>
                  </AnimatedOnScroll>
                </>
              )}
            </div>
          </div>
        </AnimatedOnScroll>

        {/* Return Process */}
        <AnimatedOnScroll animationClass="animate-fade-in-left" delay={600}>
          <div className="border-b border-gray-200 pb-6">
            <h2 className="text-2xl font-medium text-gray-900 mb-4">
              Return Process
            </h2>
            <div className="space-y-3">
              <AnimatedOnScroll animationClass="animate-fade-in-up" delay={700}>
                <p className="text-base text-gray-700 leading-relaxed">
                  {policyData?.returnProcess || 'To apply for a return, please contact our customer service team. Mention your order number and the reason for return. We will review your request and inform you about the next steps.'}
                </p>
              </AnimatedOnScroll>
              <AnimatedOnScroll animationClass="animate-fade-in-up" delay={750}>
                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Note:</span> Return shipping costs are generally borne by the customer, unless the product is defective or incorrect.
                  </p>
                </div>
              </AnimatedOnScroll>
            </div>
          </div>
        </AnimatedOnScroll>

        {/* Processing Time */}
        <AnimatedOnScroll animationClass="animate-fade-in-left" delay={800}>
          <div className="border-b border-gray-200 pb-6">
            <h2 className="text-2xl font-medium text-gray-900 mb-4">
              Return Processing Time
            </h2>
            <AnimatedOnScroll animationClass="animate-fade-in-up" delay={900}>
              <p className="text-base text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{
                __html: policyData?.processingTime || 'Once we receive and inspect the returned product, your refund process may take <span class="font-semibold">5-7 business days</span> to complete. The refund method will depend on your original payment method.'
              }}></p>
            </AnimatedOnScroll>
          </div>
        </AnimatedOnScroll>

        {/* Damaged Products */}
        <AnimatedOnScroll animationClass="animate-fade-in-left" delay={1000}>
          <div className="border-b border-gray-200 pb-6">
            <h2 className="text-2xl font-medium text-gray-900 mb-4">
              Damaged or Defective Products
            </h2>
            <AnimatedOnScroll animationClass="animate-fade-in-up" delay={1100}>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6">
                <p className="text-base text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{
                  __html: policyData?.damagedProducts || 'If you receive a damaged or defective product, please inform us within <span class="font-semibold">48 hours</span> of delivery. In such cases, we will arrange a replacement product or a full refund for you.'
                }}></p>
              </div>
            </AnimatedOnScroll>
          </div>
        </AnimatedOnScroll>

        {/* Footer Note */}
        <AnimatedOnScroll animationClass="animate-fade-in-up" delay={1200}>
          <div className="bg-gray-50 p-6 rounded border border-gray-200">
            <p className="text-base text-gray-700 text-center">
              {policyData?.footerNote || 'If you have any questions about our return policy, please feel free to contact us.'}
            </p>
          </div>
        </AnimatedOnScroll>
      </div>
    </div>
  );
};

export default RefundPolicyPage;